import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Button,
    Grid,
    Chip,
    Card,
    CardContent,
    Divider,
    IconButton,
    Tooltip,
    Alert,
    AlertTitle,
    Checkbox,
    ListItemText
} from '@mui/material';
import {
    DateRange,
    FilterList,
    Refresh,
    Download,
    Fullscreen,
    FullscreenExit,
    Info,
    Add,
    Close
} from '@mui/icons-material';
import ReactApexChart from 'react-apexcharts';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import useApi from '../../../shared/hooks/useApi';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../../../theme';
import { useSimulation } from '../../context/SimulationContext';

export default function DispatchResults() {
    const { apiGet, apiPost } = useApi();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    
    // Get simulations from context
    const { simulations: contextSimulations, isLoading: isLoadingSimulations, error: simulationsError } = useSimulation();

    // LocalStorage keys
    const CACHE_KEY_SIMULATION = 'dispatchResults_selectedSimulation';
    const CACHE_KEY_HEADERS = 'dispatchResults_selectedHeaders';
    const CACHE_KEY_CHARTS = 'dispatchResults_additionalCharts';

    // Load cached values from localStorage
    const loadCachedValue = (key, defaultValue) => {
        try {
            const cached = localStorage.getItem(key);
            return cached ? JSON.parse(cached) : defaultValue;
        } catch (error) {
            console.error(`Error loading cached value for ${key}:`, error);
            return defaultValue;
        }
    };

    // Save value to localStorage
    const saveCachedValue = (key, value) => {
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
            console.error(`Error saving cached value for ${key}:`, error);
        }
    };

    // State management with cached initial values
    const [selectedSimulation, setSelectedSimulation] = useState(() => loadCachedValue(CACHE_KEY_SIMULATION, ''));
    const [loading, setLoading] = useState(false);
    const [fullscreenChart, setFullscreenChart] = useState(null);
    const [simulationData, setSimulationData] = useState(null);

    // Use simulations from context instead of local state
    const simulations = contextSimulations || [];
    
    // State for the 3 select arrays from callExtractSimulationDataHeaders
    const [headerOptions, setHeaderOptions] = useState({
        array1: [],
        array2: [],
        allDataTypes: [] // All data types for charts
    });
    const [selectedHeaders, setSelectedHeaders] = useState(() => loadCachedValue(CACHE_KEY_HEADERS, {
        select1: '',
        select2: ''
    }));
    
    // State for additional dynamic charts - only restore selections, not data
    const [additionalCharts, setAdditionalCharts] = useState(() => {
        const cached = loadCachedValue(CACHE_KEY_CHARTS, []);
        // Only restore chart structure and selections, not the actual data
        return cached.map(chart => ({
            ...chart,
            series: [],
            options: {},
            loading: false,
            needsAutoLoad: true // Mark cached charts for auto-loading
        }));
    });

    // Track if initial auto-load has been done
    const [hasAutoLoaded, setHasAutoLoaded] = useState(false);

    // Cache selected simulation whenever it changes
    useEffect(() => {
        if (selectedSimulation) {
            saveCachedValue(CACHE_KEY_SIMULATION, selectedSimulation);
        }
    }, [selectedSimulation]);

    // Cache selected headers whenever they change
    useEffect(() => {
        saveCachedValue(CACHE_KEY_HEADERS, selectedHeaders);
    }, [selectedHeaders]);

    // Cache additional charts selections whenever they change (not the data)
    useEffect(() => {
        // Only save chart IDs and dataType selections, not series/options
        const chartsToCache = additionalCharts.map(chart => ({
            id: chart.id,
            dataType: chart.dataType
        }));
        saveCachedValue(CACHE_KEY_CHARTS, chartsToCache);
    }, [additionalCharts]);

    // Auto-load cached charts ONLY on initial mount when everything is ready
    useEffect(() => {
        // Only run once on initial mount
        if (hasAutoLoaded) {
            return;
        }

        // Wait for simulations to be loaded first
        if (isLoadingSimulations || !simulations || simulations.length === 0) {
            return;
        }

        // Check if we have all required filters
        if (!selectedSimulation || !selectedHeaders.select1 || !selectedHeaders.select2) {
            return;
        }

        // Check if headerOptions are loaded (configuration and period arrays should have data)
        if (headerOptions.array1.length === 0 || headerOptions.array2.length === 0) {
            return;
        }

        // Find charts that need auto-loading (marked with needsAutoLoad flag from cache)
        const chartsToLoad = additionalCharts.filter(chart => 
            chart.needsAutoLoad &&
            chart.dataType && 
            chart.dataType.length > 0 && 
            (!chart.series || chart.series.length === 0) &&
            !chart.loading
        );

        if (chartsToLoad.length > 0) {
            console.log(`Auto-loading ${chartsToLoad.length} cached charts after simulations loaded...`);
            chartsToLoad.forEach(chart => {
                loadSingleAdditionalChartData(chart.id, chart.dataType);
            });
            
            // Mark as auto-loaded and remove needsAutoLoad flags
            setHasAutoLoaded(true);
            setAdditionalCharts(prev => prev.map(chart => ({
                ...chart,
                needsAutoLoad: false
            })));
        } else if (additionalCharts.length === 0 || !additionalCharts.some(c => c.needsAutoLoad)) {
            // No charts to auto-load, mark as done to prevent further checks
            setHasAutoLoaded(true);
        }
    }, [
        selectedSimulation, 
        selectedHeaders.select1, 
        selectedHeaders.select2, 
        hasAutoLoaded, 
        isLoadingSimulations, 
        simulations.length,
        headerOptions.array1.length,
        headerOptions.array2.length,
        additionalCharts.length
    ]);

    // Load headerOptions whenever selectedSimulation changes
    useEffect(() => {
        if (!selectedSimulation || isLoadingSimulations || simulations.length === 0) {
            return;
        }

        // Find the selected simulation and parse its message to extract filters
        const selectedSim = simulations.find(sim => sim.event_id === selectedSimulation);
        if (selectedSim && selectedSim.message) {
            try {
                const messageData = JSON.parse(selectedSim.message);
                if (messageData['ux-filters']) {
                    const filters = messageData['ux-filters'];
                    const allDataTypes = filters['data-type'] || [];
                    
                    setHeaderOptions({
                        array1: filters.configuration || [],
                        array2: filters.period || [],
                        allDataTypes: allDataTypes
                    });
                    
                    console.log("Loaded filters for selected simulation:", filters);
                }
            } catch (parseError) {
                console.error("Error parsing simulation message:", parseError);
            }
        }
    }, [selectedSimulation, simulations, isLoadingSimulations]);

    // Auto-select first simulation when context loads simulations (only if no cached selection)
    useEffect(() => {
        if (!isLoadingSimulations && simulations && simulations.length > 0 && !selectedSimulation) {
            const firstSim = simulations[0];
            setSelectedSimulation(firstSim.event_id);
            
            // Only reset headers if no cached values
            const cachedHeaders = loadCachedValue(CACHE_KEY_HEADERS, null);
            if (!cachedHeaders || (!cachedHeaders.select1 && !cachedHeaders.select2)) {
                setSelectedHeaders({
                    select1: '',
                    select2: ''
                });
            }
        }
    }, [simulations, isLoadingSimulations, selectedSimulation]);
   

    // Chart options
    const getChartOptions = (title, yAxisTitle, isMultiSeries = false, chartId = null) => ({
        chart: {
            id: chartId, // Important for syncing
            type: 'line',
            height: 280,
            group: 'synced-charts', // Always use the same group for syncing
            toolbar: {
                show: true,
                tools: {
                    download: true,
                    selection: true,
                    zoom: true,
                    zoomin: true,
                    zoomout: true,
                    pan: true,
                    reset: true
                }
            },
            zoom: {
                enabled: true,
                type: 'x',
                autoScaleYaxis: true,
                allowMouseWheelZoom: false
            },
            selection: {
                enabled: true,
                type: 'x',
                fill: {
                    color: '#667eea',
                    opacity: 0.1
                },
                stroke: {
                    width: 1,
                    dashArray: 3,
                    color: '#667eea',
                    opacity: 0.4
                },
                xaxis: {
                    min: undefined,
                    max: undefined
                }
            },
            animations: {
                enabled: true,
                speed: 300
            }
        },
        // Don't set colors here - let series define their own colors
        stroke: {
            curve: 'stepline',
            width: 3 // Increased width for better visibility
        },
        markers: {
            size: 0, // Remove dots/markers
            hover: {
                size: 0
            }
        },
        xaxis: {
            type: 'datetime',
            title: {
                text: 'Time'
            },
            labels: {
                datetimeUTC: false, // Use local timezone
                format: 'dd MMM HH:mm'
            },
            tooltip: {
                format: 'dd MMM yyyy HH:mm'
            }
        },
        yaxis: {
            title: {
                text: yAxisTitle
            },
            labels: {
                formatter: function (val) {
                    return parseFloat(val).toFixed(2)
                }
            }
        },
        title: {
            text: title,
            align: 'left',
            style: {
                fontSize: '14px', // Smaller title
                fontWeight: 'bold'
            }
        },
        grid: {
            borderColor: '#f1f1f1'
        },
        tooltip: {
            x: {
                format: 'dd MMM yyyy HH:mm'
            },
            y: {
                formatter: function (val) {
                    return parseFloat(val).toFixed(2)
                }
            }
        },
        dataLabels: {
            enabled: false
        },
        legend: {
            show: isMultiSeries,
            position: 'top',
            horizontalAlign: 'right',
            fontSize: '12px',
            markers: {
                width: 12,
                height: 12,
                radius: 2
            },
            itemMargin: {
                horizontal: 10,
                vertical: 5
            },
            onItemClick: {
                toggleDataSeries: true
            },
            onItemHover: {
                highlightDataSeries: true
            }
        }
    });

    // Fetch simulation data
    const fetchSimulationData = async (simulationId, headers = null) => {
        try {
            if (!simulationId) {
                console.log("No simulation ID provided");
                return null;
            }
            //add also simulation messages s3_key
            // Build query parameters
            let selectedSimulation = simulations.find(simulation => simulation.event_id === simulationId);
            let s3_key = null;
            if (selectedSimulation && selectedSimulation.message) {
                try {
                    // Remove BOM and other invisible characters
                    let cleanMessage = selectedSimulation.message.trim().replace(/^\uFEFF/, '').replace(/^\u200B/, '');
                    let messageData = JSON.parse(cleanMessage);
                    s3_key = messageData.s3_key;
                } catch (error) {
                    console.error("Error parsing simulation message:", error);
                }
            }
            
            let queryParams = `simulation_id=${encodeURIComponent(simulationId)}`;
            if (s3_key) {
                queryParams += `&s3_key=${encodeURIComponent(s3_key)}`;
            }
            if (headers) {
                if (headers.select1) queryParams += `&config=${encodeURIComponent(headers.select1)}`;
                if (headers.select2) queryParams += `&period=${encodeURIComponent(headers.select2)}`;
                if (headers.select3) queryParams += `&data_type=${encodeURIComponent(headers.select3)}`;
            }
            
            
            const parquetResponse = await apiGet("UX_HTTP_API", `/esid-25-006/get-simulation-parquet-file?${queryParams}`);
            
            
            // Log sampling info if data was sampled
            if (parquetResponse?.simulation?.data_info) {
                const info = parquetResponse.simulation.data_info;
                if (info.is_sampled) {
                    console.warn(`⚠️ Data was automatically sampled to fit API response limits. Showing ${info.returned_rows} of ${info.total_rows} rows.`);
                }
            }
            
            return parquetResponse;
        } catch (error) {
            console.error("Error fetching simulation data:", error);
            return null;
        }
    };

    // Parse parquet simulation data
    const parseParquetSimulationData = (parquetResponse) => {
        
        if (!parquetResponse?.simulation?.simulation_data) {
            console.log("No simulation data found in parquet response");
            return [];
        }

        const simulationData = parquetResponse.simulation.simulation_data;
        
        // Define an expanded color palette with vibrant, distinct colors
        const colorPalette = [
            '#1E88E5',  // Bright Blue
            '#43A047',  // Green
            '#E53935',  // Red
            '#FB8C00',  // Orange
            '#8E24AA',  // Purple
            '#00ACC1',  // Cyan
            '#FDD835',  // Yellow
            '#D81B60',  // Pink
            '#3949AB',  // Indigo
            '#00897B',  // Teal
            '#F4511E',  // Deep Orange
            '#6D4C41',  // Brown
            '#5E35B1',  // Deep Purple
            '#00796B',  // Dark Teal
            '#C62828',  // Dark Red
            '#F57C00',  // Dark Orange
            '#7B1FA2',  // Dark Purple
            '#0277BD',  // Dark Blue
            '#558B2F',  // Dark Green
            '#AD1457',  // Dark Pink
        ];
        
        // NEW: Handle multiple metrics response format (dictionary of metrics)
        if (simulationData && typeof simulationData === 'object' && !Array.isArray(simulationData)) {
            console.log("Detected multiple metrics format (dictionary)");
            const series = [];
            let colorIndex = 0;
            
            // Each key is a metric name, each value is an array of {timestamp, value}
            Object.entries(simulationData).forEach(([metricName, metricData]) => {
                if (Array.isArray(metricData) && metricData.length > 0) {
                    // Convert to chart format - filter out null timestamps first
                    const seriesData = metricData
                        .filter(item => item.timestamp !== null && item.timestamp !== undefined)
                        .map(item => ({
                            x: new Date(item.timestamp).getTime(),
                            y: item.value !== null && item.value !== undefined ? parseFloat(item.value) : 0
                        }))
                        .filter(item => !isNaN(item.x)) // Remove invalid timestamps
                        .sort((a, b) => a.x - b.x);
                    
                    // Clean metric name for display
                    const cleanName = metricName
                        .split('_')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ');
                    
                    const color = colorPalette[colorIndex % colorPalette.length];
                    colorIndex++;
                    
                    console.log(`Parsed metric "${cleanName}" with ${seriesData.length} data points, color: ${color}`);
                    
                    series.push({
                        name: cleanName,
                        data: seriesData,
                        color: color
                    });
                }
            });
            
            console.log(`Parsed ${series.length} metrics from dictionary format`);
            return series;
        }
        
        // ORIGINAL: If simulation_data is an array (single metric or old parquet format), process it
        if (Array.isArray(simulationData)) {
            const series = [];
            
            // Check if this is the simple format: [{timestamp, value}, ...]
            if (simulationData.length > 0 && simulationData[0].timestamp && simulationData[0].value !== undefined && !simulationData[0].column) {
                console.log("Detected simple array format (timestamp + value)");
                
                // Convert to chart format - filter out null timestamps first
                const seriesData = simulationData
                    .filter(item => item.timestamp !== null && item.timestamp !== undefined)
                    .map(item => ({
                        x: new Date(item.timestamp).getTime(),
                        y: item.value !== null && item.value !== undefined ? parseFloat(item.value) : 0
                    }))
                    .filter(item => !isNaN(item.x)) // Remove invalid timestamps
                    .sort((a, b) => a.x - b.x);
                
                const color = colorPalette[0];
                
                console.log(`Parsed single metric with ${seriesData.length} data points, color: ${color}`);
                if (seriesData.length > 0) {
                    console.log(`First timestamp: ${new Date(seriesData[0].x).toISOString()}, value: ${seriesData[0].y}`);
                    console.log(`Last timestamp: ${new Date(seriesData[seriesData.length - 1].x).toISOString()}, value: ${seriesData[seriesData.length - 1].y}`);
                }
                
                series.push({
                    name: 'Data',
                    data: seriesData,
                    color: color
                });
                
                return series;
            }
            
            // Otherwise, handle the column-based format
            const dataByColumn = {};
            
            // Group data by column name
            simulationData.forEach(item => {
                // Remove the timestamp suffix from the column name to properly group data
                // Column format: "results.0.5MW / 1MWh.2024-07.bess_discharge_efficiency.2024-07-01T00:00:00+02:00"
                // We want to remove the last timestamp part to group by the actual data type
                let columnName = item.column;
                
                // Split by dots and remove the last part if it looks like a timestamp
                const parts = columnName.split('.');
                if (parts.length > 4) {
                    // Check if the last part looks like a timestamp (contains 'T' and ':')
                    const lastPart = parts[parts.length - 1];
                    if (lastPart.includes('T') && lastPart.includes(':')) {
                        // Rejoin without the timestamp part
                        columnName = parts.slice(0, -1).join('.');
                    }
                }
                
                if (!dataByColumn[columnName]) {
                    dataByColumn[columnName] = [];
                }
                // Only add valid timestamps
                if (item.timestamp !== null && item.timestamp !== undefined) {
                    const timestamp = new Date(item.timestamp).getTime();
                    if (!isNaN(timestamp)) {
                        dataByColumn[columnName].push({
                            x: timestamp,
                            y: item.value !== null && item.value !== undefined ? parseFloat(item.value) : 0
                        });
                    }
                }
            });
            
            // Create series for each column
            Object.entries(dataByColumn).forEach(([columnName, data], index) => {
                // Extract a cleaner name from the column
                // For format like "results.0.5MW / 1MWh.2024-07.bess_discharge_efficiency"
                const nameParts = columnName.split('.');
                
                // The data_type is always the last part after the timestamp is removed
                // e.g., "results.0.5MW / 1MWh.2024-07.bess_discharge_efficiency" -> "bess_discharge_efficiency"
                let cleanName = nameParts[nameParts.length - 1];
                
                // Make the name more readable - convert underscores to spaces and capitalize
                cleanName = cleanName
                    .split('_')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
                
                // Assign colors from palette based on index to ensure different colors
                const color = colorPalette[index % colorPalette.length];
                
                // Sort data by timestamp
                const sortedData = data.sort((a, b) => a.x - b.x);
                
                console.log(`Creating series "${cleanName}" with ${sortedData.length} data points, color: ${color}`);
                console.log(`First timestamp: ${new Date(sortedData[0].x).toISOString()}, value: ${sortedData[0].y}`);
                console.log(`Last timestamp: ${new Date(sortedData[sortedData.length - 1].x).toISOString()}, value: ${sortedData[sortedData.length - 1].y}`);
                
                series.push({
                    name: cleanName,
                    data: sortedData,
                    color: color
                });
            });
            
            console.log(`Parsed ${series.length} series from parquet data with total data points:`, 
                series.reduce((sum, s) => sum + s.data.length, 0));
            return series;
        }
        
        return [];
    };


    // Effects
    // Note: Auto-loading is removed for performance. Users must click "Show" buttons.
    
    // Load data for a specific additional chart when its data type changes
    const loadSingleAdditionalChartData = async (chartId, dataTypes) => {
        if (!selectedSimulation || !selectedHeaders.select1 || !selectedHeaders.select2 || !dataTypes || dataTypes.length === 0) {
            return;
        }
        
        // Set loading state for this specific chart
        setAdditionalCharts(prev => prev.map(chart => 
            chart.id === chartId ? { ...chart, loading: true } : chart
        ));
        
        try {
            console.log(`Loading data for chart ${chartId} with ${dataTypes.length} data types`);
            
            // NEW: Join multiple data types with commas for single request
            const dataTypesString = dataTypes.join(',');
            console.log(`Fetching data types in single request: ${dataTypesString}`);
            
            const simulationData = await fetchSimulationData(selectedSimulation, {
                select1: selectedHeaders.select1,
                select2: selectedHeaders.select2,
                select3: dataTypesString
            });
            
            if (simulationData) {
                // Parse results data (handles both single and multiple metrics)
                const series = parseParquetSimulationData(simulationData);
                
                const chartOptions = getChartOptions(
                    `Custom Data`,
                    'Value',
                    series.length > 1,
                    chartId // Pass chart ID for syncing
                );
                
                // Update only the specific chart
                setAdditionalCharts(prev => prev.map(chart => 
                    chart.id === chartId ? {
                        ...chart,
                        series: series,
                        options: chartOptions,
                        loading: false
                    } : chart
                ));
                
                console.log(`Successfully loaded data for chart ${chartId} with ${series.length} series`);
            }
        } catch (error) {
            console.error(`Error loading data for chart ${chartId}:`, error);
            // Reset loading state on error
            setAdditionalCharts(prev => prev.map(chart => 
                chart.id === chartId ? { ...chart, loading: false } : chart
            ));
        }
    };

    // Handlers
    const handleSimulationChange = (event) => {
        console.log("handleSimulationChange", event.target.value);
        console.log("event.target", event.target);
        const simulationId = event.target.value;
        setSelectedSimulation(simulationId);
        
        // Clear existing data when simulation changes
        setSimulationData(null);
        // Clear additional charts when simulation changes
        setAdditionalCharts([]);
        
        // Reset header selections when new simulation is selected
        setSelectedHeaders({
            select1: '',
            select2: ''
        });
        
        // Note: headerOptions will be automatically loaded by the useEffect that watches selectedSimulation
    };

    const handleHeaderChange = (selectName, value) => {
        console.log(`Header selection changed: ${selectName} = ${value}`);
        setSelectedHeaders(prev => ({
            ...prev,
            [selectName]: value
        }));
    };

    const handleFullscreen = (chartType) => {
        setFullscreenChart(fullscreenChart === chartType ? null : chartType);
    };
    
    // Add a new chart
    const handleAddChart = () => {
        if (additionalCharts.length >= 4) {
            return; // Maximum 4 charts
        }
        const newChart = {
            id: `chart_${Date.now()}`,
            dataType: [], // Changed to array for multi-selection
            series: [],
            options: {},
            loading: false,
            needsAutoLoad: false // New charts should not auto-load
        };
        setAdditionalCharts(prev => [...prev, newChart]);
    };
    
    // Remove a chart
    const handleRemoveChart = (chartId) => {
        setAdditionalCharts(prev => prev.filter(chart => chart.id !== chartId));
    };
    
    // Update chart data type selection (data will be loaded when user clicks Show button)
    const handleChartDataTypeChange = (chartId, dataType) => {
        // Update the chart's dataType
        setAdditionalCharts(prev => prev.map(chart => 
            chart.id === chartId ? { ...chart, dataType } : chart
        ));
    };

    // Reset zoom on all charts
    const handleResetAllZoom = () => {
        // Method 1: Try to use ApexCharts exec API
        if (window.ApexCharts) {
            // Reset each chart individually using its ID
            additionalCharts.forEach((chart, index) => {
                if (chart.id) {
                    try {
                        // Try multiple methods to ensure it works
                        window.ApexCharts.exec(chart.id, 'zoomX');
                    } catch (err) {
                        console.log(`Method 1 failed for chart ${chart.id}:`, err);
                    }
                }
            });
        }
        
        // Method 2: Update chart options to force reset by re-rendering
        setAdditionalCharts(prev => prev.map(chart => {
            if (chart.options && chart.series && chart.series.length > 0) {
                // Create new options object with reset xaxis
                const newOptions = {
                    ...chart.options,
                    xaxis: {
                        ...chart.options.xaxis,
                        min: undefined,
                        max: undefined
                    }
                };
                return { 
                    ...chart, 
                    options: newOptions,
                    // Add a key to force re-render
                    _resetKey: Date.now()
                };
            }
            return chart;
        }));
    };

    // Reset all cached selections and charts
    const handleResetAllSelections = () => {
        // Clear localStorage
        localStorage.removeItem(CACHE_KEY_SIMULATION);
        localStorage.removeItem(CACHE_KEY_HEADERS);
        localStorage.removeItem(CACHE_KEY_CHARTS);
        
        // Reset state to defaults
        setSelectedSimulation('');
        setSelectedHeaders({
            select1: '',
            select2: ''
        });
        setAdditionalCharts([]);
        setSimulationData(null);
        setHeaderOptions({
            array1: [],
            array2: [],
            allDataTypes: []
        });
        
        // Reset auto-load flag so cached charts can be auto-loaded again if needed
        setHasAutoLoaded(false);
        
        console.log('All selections and charts have been reset');
    };


    return (
        <Box sx={{ 
            padding: 3, 
            marginLeft: 5,
            '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
            }
        }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
                        Dispatch Results
                    </Typography>
                    {simulationData && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                            Showing data for: {simulationData.simulation?.name || 'Unknown Simulation'} 
                            {simulationData.simulation?.status && (
                                <Chip 
                                    label={simulationData.simulation.status} 
                                    size="small" 
                                    color={simulationData.simulation.status === 'completed' ? 'success' : 'warning'}
                                    sx={{ ml: 1 }}
                                />
                            )}
                        </Typography>
                    )}
                </Box>
                
                {/* Chart Control Buttons */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {additionalCharts.length > 0 && (
                        <Chip
                            icon={<Info />}
                            label="Charts are synchronized"
                            size="small"
                            sx={{
                                backgroundColor: 'rgba(102, 126, 234, 0.1)',
                                color: colors.entras_purple[100],
                                fontWeight: 600,
                                border: `1px solid ${colors.entras_purple[100]}`,
                                '& .MuiChip-icon': {
                                    color: colors.entras_purple[100]
                                }
                            }}
                        />
                    )}
                    
                    <Tooltip title="Reset zoom on all synchronized charts">
                        <span>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleResetAllZoom}
                                disabled={additionalCharts.length === 0}
                                sx={{
                                    minWidth: '140px',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '13px',
                                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #5568d3 0%, #65408b 100%)',
                                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
                                    },
                                    '&:disabled': {
                                        background: 'rgba(0, 0, 0, 0.12)',
                                        color: 'rgba(0, 0, 0, 0.26)'
                                    }
                                }}
                            >
                                🔍 Reset Zoom
                            </Button>
                        </span>
                    </Tooltip>
                    
                    <Tooltip title="Clear all cached selections and charts">
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleResetAllSelections}
                            sx={{
                                minWidth: '140px',
                                borderColor: '#f44336',
                                color: '#f44336',
                                fontWeight: 600,
                                fontSize: '13px',
                                borderWidth: 2,
                                '&:hover': {
                                    borderWidth: 2,
                                    borderColor: '#d32f2f',
                                    backgroundColor: 'rgba(244, 67, 54, 0.08)'
                                }
                            }}
                        >
                            🗑️ Reset All
                        </Button>
                    </Tooltip>
                </Box>
            </Box>
            <Paper 
                elevation={2}
                sx={{ 
                    p: 3, 
                    mb: 3,
                    //background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eaf6 100%)',
                    borderRadius: 2,
                    border: '1px solid rgba(102, 126, 234, 0.2)'
                }}
            >
                <Typography 
                    variant="subtitle2" 
                    sx={{ 
                        color: colors.entras_purple[100], 
                        mb: 2, 
                        fontWeight: 700,
                        letterSpacing: '1px',
                        fontSize: '13px'
                    }}
                >
                    ⚙️ FILTERS & SETTINGS
                </Typography>
                <Grid container spacing={2} alignItems="center">
                    {/* Simulation Selection */}
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small" sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.95)'
                                }
                            }
                        }}>
                            <InputLabel>Simulation</InputLabel>
                            <Select
                                value={selectedSimulation}
                                onChange={handleSimulationChange}
                                label="Simulation"
                            >
                                {simulations.map((sim) => {
                                    // Extract name and status from message if available
                                    let simulationName = sim.name || "Unnamed Simulation";
                                    let simulationStatus = sim.status || "Unknown";
                                    
                                    if (sim.message) {
                                        try {
                                            const messageData = JSON.parse(sim.message);
                                            simulationName = messageData.name || sim.name || "Unnamed Simulation";
                                            simulationStatus = messageData.status || sim.status || "Unknown";
                                        } catch (error) {
                                            console.error("Error parsing simulation message:", error);
                                        }
                                    }
                                    
                                    return (
                                        <MenuItem key={sim.event_id} value={sim.event_id}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2" color="#2c3e50" fontSize="0.85rem">
                                                    {simulationName}
                                                </Typography>
                                                <Chip
                                                    label={simulationStatus}
                                                    size="small"
                                                    color={simulationStatus.toLowerCase() === 'completed' ? 'success' : simulationStatus.toLowerCase() === 'failed' ? 'error' : 
                                                        simulationStatus.toLowerCase() === 'error' ? 'error' : 'warning'}
                                                    sx={{
                                                        color: 'white',
                                                        width: '100px',
                                                    }}
                                                />
                                            </Box>
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Header Select 1 - Configuration */}
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small" disabled={!selectedSimulation || headerOptions.array1.length === 0} sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.95)'
                                }
                            }
                        }}>
                            <InputLabel>Configuration</InputLabel>
                            <Select
                                value={selectedHeaders.select1}
                                onChange={(e) => handleHeaderChange('select1', e.target.value)}
                                label="Configuration"
                            >
                                {headerOptions.array1.map((option, index) => (
                                    <MenuItem key={index} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Header Select 2 - Period */}
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small" disabled={!selectedSimulation || headerOptions.array2.length === 0} sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.95)'
                                }
                            }
                        }}>
                            <InputLabel>Period</InputLabel>
                            <Select
                                value={selectedHeaders.select2}
                                onChange={(e) => handleHeaderChange('select2', e.target.value)}
                                label="Period"
                            >
                                {headerOptions.array2.map((option, index) => (
                                    <MenuItem key={index} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            <Grid container spacing={2}>
                {/* Additional Dynamic Charts */}
                {additionalCharts.map((chart, index) => (
                    <Grid item xs={12} key={chart.id}>
                        <Card 
                            elevation={3} 
                            sx={{ 
                                borderRadius: 2,
                                border: chart.loading ? `2px solid ${colors.entras_blue[100]}` : 'none',
                                transition: 'all 0.3s ease'
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography 
                                                variant="h6" 
                                                sx={{ 
                                                    fontSize: '18px',
                                                    fontWeight: 700,
                                                    color: chart.loading ? colors.entras_blue[100] : colors.entras_purple[100],
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1
                                                }}
                                            >
                                                📊 Chart {index + 1}
                                            </Typography>
                                           
                                        </Box>
                                        <FormControl size="small" sx={{ minWidth: 250 }} disabled={!selectedSimulation || headerOptions.allDataTypes.length === 0 || chart.loading}>
                                            <InputLabel>Data Type</InputLabel>
                                            <Select
                                                multiple
                                                value={chart.dataType}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    // Limit selection to 4 items
                                                    if (value.length <= 4) {
                                                        handleChartDataTypeChange(chart.id, value);
                                                    }
                                                }}
                                                label="Data Type"
                                                renderValue={(selected) => (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {selected.map((value) => (
                                                            <Chip 
                                                                key={value} 
                                                                label={value} 
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: '#43A047',
                                                                    color: 'white',
                                                                    fontWeight: 600,
                                                                    fontSize: '12px',
                                                                    boxShadow: '0 2px 4px rgba(67, 160, 71, 0.3)'
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                )}
                                            >
                                                {headerOptions.allDataTypes.map((option, index) => (
                                                    <MenuItem 
                                                        key={index} 
                                                        value={option}
                                                        disabled={chart.dataType.length >= 4 && chart.dataType.indexOf(option) === -1}
                                                    >
                                                        <Checkbox 
                                                            checked={chart.dataType.indexOf(option) > -1}
                                                            sx={{
                                                                color: '#43A047',
                                                                '&.Mui-checked': {
                                                                    color: '#43A047'
                                                                }
                                                            }}
                                                        />
                                                        <ListItemText primary={option} />
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <Button
                                            variant="contained"
                                            size="medium"
                                            onClick={() => loadSingleAdditionalChartData(chart.id, chart.dataType)}
                                            disabled={
                                                !selectedSimulation || 
                                                !selectedHeaders.select1 || 
                                                !selectedHeaders.select2 || 
                                                !chart.dataType || 
                                                chart.dataType.length === 0 ||
                                                chart.loading
                                            }
                                            sx={{
                                                minWidth: '120px',
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                color: 'white',
                                                fontWeight: 700,
                                                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                                                letterSpacing: '0.5px',
                                                '&:hover': {
                                                    background: 'linear-gradient(135deg, #5568d3 0%, #65408b 100%)',
                                                    boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)',
                                                    transform: 'translateY(-2px)'
                                                },
                                                '&:disabled': {
                                                    background: 'rgba(0, 0, 0, 0.12)',
                                                    color: 'rgba(0, 0, 0, 0.26)'
                                                },
                                                transition: 'all 0.3s ease'
                                            }}
                                        >
                                            {chart.loading ? 'Loading...' : 'Show Chart'}
                                        </Button>
                                    </Box>
                                    <Box>
                                        <Tooltip title="Toggle Fullscreen">
                                            <IconButton 
                                                size="small" 
                                                onClick={() => handleFullscreen(chart.id)} 
                                                disabled={chart.loading}
                                                sx={{
                                                    color: colors.entras_purple[100],
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(118, 75, 162, 0.1)'
                                                    }
                                                }}
                                            >
                                                {fullscreenChart === chart.id ? <FullscreenExit /> : <Fullscreen />}
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Remove Chart">
                                            <IconButton 
                                                size="small" 
                                                onClick={() => handleRemoveChart(chart.id)} 
                                                disabled={chart.loading}
                                                sx={{
                                                    color: '#f44336',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(244, 67, 54, 0.1)'
                                                    }
                                                }}
                                            >
                                                <Close />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                                <Divider sx={{ mb: 2, borderColor: 'rgba(0,0,0,0.08)' }} />
                                {chart.loading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 280 }}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography 
                                                variant="h6" 
                                                sx={{ 
                                                    mb: 2, 
                                                    color: colors.entras_blue[100],
                                                    fontWeight: 700
                                                }}
                                            >
                                                ⏳ Loading chart {index + 1} data...
                                            </Typography>
                                            <Box sx={{ 
                                                width: 50, 
                                                height: 50, 
                                                border: '5px solid rgba(30, 136, 229, 0.1)',
                                                borderTop: `5px solid ${colors.entras_blue[100]}`,
                                                borderRadius: '50%',
                                                animation: 'spin 1s linear infinite',
                                                margin: '0 auto',
                                            }} />
                                        </Box>
                                    </Box>
                                ) : !chart.dataType || chart.dataType.length === 0 || !chart.series || chart.series.length === 0 ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 280 }}>
                                        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                                            {!chart.dataType || chart.dataType.length === 0 ? 'Please select data type(s) and click Show' : 'No data available. Click Show to load data.'}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <ReactApexChart
                                        key={chart._resetKey || chart.id}
                                        options={chart.options}
                                        series={chart.series}
                                        type="line"
                                        height={280}
                                    />
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
                
                {/* Add Chart Button */}
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<Add />}
                            onClick={handleAddChart}
                            disabled={additionalCharts.length >= 4 || !selectedSimulation}
                            sx={{
                                borderColor: '#667eea',
                                color: '#667eea',
                                fontWeight: 700,
                                px: 4,
                                py: 1.5,
                                fontSize: '16px',
                                borderWidth: 2,
                                letterSpacing: '0.5px',
                                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.15)',
                                '&:hover': {
                                    borderWidth: 2,
                                    borderColor: '#5568d3',
                                    backgroundColor: 'rgba(102, 126, 234, 0.12)',
                                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.25)',
                                    transform: 'translateY(-1px)'
                                },
                                '&:disabled': {
                                    borderColor: 'rgba(0, 0, 0, 0.12)',
                                    color: 'rgba(0, 0, 0, 0.26)',
                                    boxShadow: 'none'
                                },
                                transition: 'all 0.3s ease'
                            }}
                        >
                            Add Chart {additionalCharts.length > 0 ? `(${additionalCharts.length}/4)` : '(Max 4)'}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
           
            {/* Fullscreen Modal */}
            {fullscreenChart && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0,0,0,0.8)',
                        zIndex: 9999,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        p: 2
                    }}
                    onClick={() => setFullscreenChart(null)}
                >
                    <Box
                        sx={{
                            backgroundColor: 'white',
                            borderRadius: 2,
                            p: 3,
                            maxWidth: '90vw',
                            maxHeight: '90vh',
                            overflow: 'auto'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h5">
                                {(() => {
                                    const chartIndex = additionalCharts.findIndex(c => c.id === fullscreenChart);
                                    const chart = additionalCharts[chartIndex];
                                    if (chart) {
                                        return `Chart ${chartIndex + 1} - ${chart.dataType && chart.dataType.length > 0 ? chart.dataType.join(', ') : 'No Data'}`;
                                    }
                                    return 'Chart';
                                })()}
                            </Typography>
                            <IconButton onClick={() => setFullscreenChart(null)}>
                                <FullscreenExit />
                            </IconButton>
                        </Box>
                        <ReactApexChart
                            key={(() => {
                                const chart = additionalCharts.find(c => c.id === fullscreenChart);
                                if (chart) return chart._resetKey || chart.id;
                                return 'fullscreen';
                            })()}
                            options={(() => {
                                const chart = additionalCharts.find(c => c.id === fullscreenChart);
                                if (chart) return chart.options;
                                return {};
                            })()}
                            series={(() => {
                                const chart = additionalCharts.find(c => c.id === fullscreenChart);
                                if (chart) return chart.series;
                                return [];
                            })()}
                            type="line"
                            height={500}
                        />
                    </Box>
                </Box>
            )}
        </Box>
    );
}